import { Injectable } from '@angular/core';
import { App, AppInfo } from '@capacitor/app';

/**
 * Package name of the running shell.
 *
 * Two Android apps ship from one OTA bundle: com.demoemre.app (the original,
 * published under a developer's personal Play account) and com.sagel.app (the
 * company account). Both carry the same baseUrl and the same request paths, so
 * the backend cannot tell them apart unless the client names itself. That name
 * picks the push-service container holding the app's Firebase credentials — an
 * FCM token minted in one Firebase project cannot be sent from the other. (V109)
 *
 * getAppId() returns null when the identity is unknown: on web, and if the
 * native call fails. Callers decide what null means, and the two call sites
 * deliberately disagree — see their comments.
 */
@Injectable({ providedIn: 'root' })
export class AppIdentityService {

  private cachedAppId: string | null = null;

  async getAppId(): Promise<string | null> {
    if (this.cachedAppId) {
      return this.cachedAppId;
    }

    try {
      const info = await this.getInfo();
      this.cachedAppId = info?.id || null;
      return this.cachedAppId;
    } catch (err) {
      // Left uncached on purpose: a failed lookup is not an answer, and the
      // next caller should ask again rather than inherit this one's bad luck.
      console.warn('[AppIdentity] App.getInfo() failed, app identity unknown.', err);
      return null;
    }
  }

  /**
   * Seam for tests. Capacitor plugin objects are Proxies carrying only a `get`
   * trap, which ignores the target — assigning over a plugin method has no
   * effect, so App.getInfo cannot be spied on directly.
   */
  protected getInfo(): Promise<AppInfo> {
    return App.getInfo();
  }
}
